import { useMemo, useState } from 'react'
import { Box, Typography, Link as MuiLink } from '@mui/material'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import StarIcon from '@mui/icons-material/Star'
import { PartnerPageData, RecentWinner } from 'types/types'
import { useRecentWinners } from 'hooks/useRecentWinners'
import { isTBLPage } from '@/utils/tblUtils'
import { PRIMARY_COLORS_VARIANTS } from '@/utils/constants/colors'

const TRUSTPILOT_GREEN = '#00b67a'
const GOLD = '#FFC107'
const DARK_BG = '#12161f7a'
const PLAYERS_TEXT_COLOR = PRIMARY_COLORS_VARIANTS[200]
const SECONDS_PER_ITEM = 4

// TODO: read from partner config once trustpilot_score/trustpilot_review_count/
// trustpilot_url are added there - same TBL Trustpilot profile the footer
// widget already links to (see components/Footer.tsx).
const TRUSTPILOT_SCORE = 4.8
const TRUSTPILOT_REVIEW_COUNT = 65
const TRUSTPILOT_URL = 'https://www.trustpilot.com/review/thebigleagues.co.uk'
const TRUSTPILOT_FILLED_STARS = Math.round(TRUSTPILOT_SCORE)
const MIN_MARQUEE_DURATION_S = 20

function formatWinnerLine(winner: RecentWinner): string {
  const amountPart = winner.amount_display ? `${winner.amount_display} ` : ''
  return `${winner.display_name} won ${amountPart}${winner.prize_label}`.toUpperCase()
}

interface SocialProofBannerProps {
  page: PartnerPageData
}

export function SocialProofBanner({ page }: SocialProofBannerProps) {
  const [isPaused, setIsPaused] = useState(false)
  const enabled = isTBLPage(page)
  const { data } = useRecentWinners(page.id, enabled)

  const winners = data?.winners ?? []
  const activePlayersCount = data?.active_players_count
  const tickerLines = useMemo(() => winners.map(formatWinnerLine), [winners])
  const marqueeDurationS = Math.max(MIN_MARQUEE_DURATION_S, tickerLines.length * SECONDS_PER_ITEM)
  const hasStatsRow = true

  if (!enabled) return null
  if (!tickerLines.length && !hasStatsRow) return null

  const pauseHandlers = {
    onMouseEnter: () => setIsPaused(true),
    onMouseLeave: () => setIsPaused(false),
    onTouchStart: () => setIsPaused(true),
    onTouchEnd: () => setIsPaused(false),
  }

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: DARK_BG,
        backdropFilter: 'blur(4px)',
        border: `1.5px solid ${GOLD}`,
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: `0 4px 16px ${GOLD}36`,
      }}
    >
      {/* Stats + Trustpilot row */}
      {hasStatsRow && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            rowGap: 1,
            columnGap: { xs: 2, sm: 4 },
            px: { xs: 2, sm: 3 },
            py: 1.25,
          }}
        >
          {activePlayersCount != null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255,193,7,0.18)',
                  flexShrink: 0,
                }}
              >
                <GroupOutlinedIcon sx={{ fontSize: '1rem', color: GOLD }} />
              </Box>
              <Typography
                sx={{ fontSize: '0.875rem', fontWeight: 700, color: PLAYERS_TEXT_COLOR }}
                noWrap
              >
                {activePlayersCount.toLocaleString('en-GB')}+ players
              </Typography>
            </Box>
          )}

          <MuiLink
            href={TRUSTPILOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: 'rgba(255,255,255,0.85)',
                textDecoration: 'underline',
              }}
            >
              Reviews {TRUSTPILOT_REVIEW_COUNT}
            </Typography>
            <Box sx={{ display: 'flex', gap: '2px' }}>
              {Array.from({ length: 5 }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 16,
                    height: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: TRUSTPILOT_GREEN,
                    borderRadius: '3px',
                  }}
                >
                  <StarIcon
                    sx={{
                      fontSize: '0.75rem',
                      color: index < TRUSTPILOT_FILLED_STARS ? 'white' : 'rgba(255,255,255,0.3)',
                    }}
                  />
                </Box>
              ))}
            </Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 800, color: 'white' }}>
              {TRUSTPILOT_SCORE}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.6875rem',
                fontWeight: 700,
                color: 'rgba(255,255,255,0.55)',
                letterSpacing: '0.04em',
              }}
            >
              TRUSTPILOT
            </Typography>
          </MuiLink>
        </Box>
      )}

      {/* Rolling winner ticker */}
      {tickerLines.length > 0 && (
        <Box
          tabIndex={0}
          role="region"
          aria-label="Recent winners"
          sx={{
            overflow: 'hidden',
            bgcolor: 'rgba(0,0,0,0.18)',
            borderTop: hasStatsRow ? '1px solid rgba(255,193,7,0.25)' : 'none',
            py: 1,
          }}
          {...pauseHandlers}
        >
          <Box
            sx={{
              display: 'flex',
              width: 'max-content',
              whiteSpace: 'nowrap',
              animationName: 'social-proof-marquee',
              animationDuration: `${marqueeDurationS}s`,
              animationTimingFunction: 'linear',
              animationIterationCount: 'infinite',
              animationPlayState: isPaused ? 'paused' : 'running',
              '@keyframes social-proof-marquee': {
                from: { transform: 'translate3d(0,0,0)' },
                to: { transform: 'translate3d(-50%,0,0)' },
              },
            }}
          >
            {[...tickerLines, ...tickerLines].map((line, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <Typography
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: 800,
                    color: 'white',
                    letterSpacing: '0.04em',
                    px: 1.5,
                  }}
                >
                  {line}
                </Typography>
                <Box component="span" sx={{ color: GOLD, fontSize: '0.7rem' }}>
                  &#8226;
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default SocialProofBanner
